import {
  Peripheral,
  Device_CommissioningState,
  Product,
  ProductType,
  Device_Mode,
  Reservoir,
  PeripheralType,
  ReuseProductConfig_ReuseType,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { create } from '@bufbuild/protobuf';
import { Command } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/command_pb';
import { MetricType } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/metrics_pb';
import { ListMetricsRequest_TargetSchema } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import type { ListMetricsRequest_Target } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/aqora_service_pb';
import { IssueModel } from '~/shared/models/issues/IssueModel';
import { DeviceModel } from '~/shared/models/system/DeviceModel';
import { PeripheralModel } from '~/shared/models/system/PeripheralModel';
import { assertIsDefined, assertUnreachable } from '~/types/assert-type';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';

const SUPPORTED_METRICS_TYPES = [
  MetricType.AIR_TEMPERATURE_CELSIUS,
  MetricType.WATER_TEMPERATURE_CELSIUS,
  MetricType.WATER_HEIGHT_MM,
  MetricType.CALCULATED_WATER_PERCENTAGE,
  MetricType.CALCULATED_VALVE_HEIGHT_PERCENTAGE,
  MetricType.CALCULATED_WATER_VOLUME_M3,
  MetricType.PRECIPITATION_MM,
  MetricType.VALVE_HEIGHT_MM,
  MetricType.BATTERY_PERCENTAGE,
  MetricType.WIND_SPEED_METER_PER_SECOND,
  MetricType.WIND_GUST_SPEED_METER_PER_SECOND,
  MetricType.WIND_DIRECTION_DEGREES,
  MetricType.WIND_GUST_DIRECTION_DEGREES,
  MetricType.CELLULAR_RSSI_DBM,
  MetricType.TAP_ON,
  MetricType.FLOW_RATE_L_SECOND,
  MetricType.PUMP_STATE_ON,
  MetricType.HUMIDITY_PERCENTAGE,
  MetricType.FLOW_DIFFERENCE_L,
  MetricType.VALVE_STATE_ON,
] as const;

export type SupportedMetricType = (typeof SUPPORTED_METRICS_TYPES)[number];

export type MetricTypeOption = {
  resourceName: string;
  type: SupportedMetricType;
};

export function isSupportedMetricType(
  metricType: MetricType,
): metricType is SupportedMetricType {
  return SUPPORTED_METRICS_TYPES.includes(metricType as SupportedMetricType);
}

const createMetricTarget = (resourceName: string, types: MetricType[]) => {
  const filteredTypes = types.filter(isSupportedMetricType);
  return create(ListMetricsRequest_TargetSchema, {
    resourceName,
    types: filteredTypes,
  });
};

export type ProductIconType =
  | 'DropIcon'
  | 'LevelIcon'
  | 'TapIcon'
  | 'CloudRainIcon'
  | 'PumpIcon'
  | 'ControllerIcon'
  | 'WaterReuseIcon'
  | 'TreeIcon'
  | 'ToiletIcon'
  | 'FireFightingIcon'
  | 'CarWashIcon'
  | 'PlantWateringIcon';

export type ProductState =
  | 'HAS_ISSUES'
  | 'READY_FOR_LINKING'
  | 'PENDING_CLOUD_CONNECTION'
  | 'READY_FOR_TESTING'
  | 'OPERATIONAL'
  | 'MAINTENANCE'
  | 'NEEDS_CONFIGURATION'
  | 'PERIPHERAL_LINKING';

export type ControlTypes = 'valve' | 'tap' | 'pump';

function deriveProductState({
  commissioningState,
  deviceMode,
  hasIssues,
  isSimulation = false,
}: {
  commissioningState: Device_CommissioningState;
  deviceMode: Device_Mode;
  hasIssues: boolean;
  isSimulation?: boolean;
}): ProductState {
  if (isSimulation) {
    return 'OPERATIONAL';
  }
  switch (commissioningState) {
    case Device_CommissioningState.READY_FOR_LINKING:
      return 'READY_FOR_LINKING';
    case Device_CommissioningState.PENDING_CLOUD_CONNECTION:
      return 'PENDING_CLOUD_CONNECTION';
    case Device_CommissioningState.READY_FOR_TESTING:
      return 'READY_FOR_TESTING';
    case Device_CommissioningState.OPERATIONAL:
      if (deviceMode === Device_Mode.MAINTENANCE) {
        return 'MAINTENANCE';
      }
      if (hasIssues) {
        return 'HAS_ISSUES';
      }
      return 'OPERATIONAL';
    case Device_CommissioningState.NEEDS_CONFIGURATION:
      return 'NEEDS_CONFIGURATION';
    case Device_CommissioningState.PERIPHERAL_LINKING:
      return 'PERIPHERAL_LINKING';
    case Device_CommissioningState.UNSPECIFIED:
      throw new Error('Unspecified commissioning state');
    default:
      // @ts-ignore
      return assertUnreachable(commissioningState);
  }
}

export type ProductInfo = {
  peripherals: Peripheral[];
  product: Product;
  issues: IssueModel[];
  device: DeviceModel;
  reservoir?: Reservoir;
  preventDeviceChange: boolean;
  isSimulation: boolean;
};

export class ProductModel {
  readonly id: string;

  readonly name: string;

  readonly type: ProductType;

  readonly icon: ProductIconType;

  readonly title: string | undefined;

  readonly maxValveHeight?: number;

  readonly reservoirTitle?: string;

  readonly reservoirName?: string;

  readonly previousProductId: string;

  readonly nextProductId: string;

  readonly availableMetricTargets: ListMetricsRequest_Target[];

  readonly availableMetricChartOptions: MetricTypeOption[];

  readonly openValveCommand: Command | undefined;

  readonly closeValveCommand: Command | undefined;

  readonly openTapCommand: Command | undefined;

  readonly closeTapCommand: Command | undefined;

  readonly maintenanceModeCommand: Command | undefined;

  readonly setValveHeightCommand: Command | undefined;

  readonly setPumpCommand: Command | undefined;

  readonly openPumpCommand: Command | undefined;

  readonly closePumpCommand: Command | undefined;

  readonly reservoirPosition?: 'top' | 'bottom' | 'side';

  readonly state: ProductState;

  readonly issues: IssueModel[];

  readonly device: DeviceModel;

  readonly allowChangingDevice: boolean = false;

  readonly showControlActions: boolean = false;

  readonly isSelectable: boolean;

  readonly controlType: ControlTypes | undefined;

  readonly peripherals: PeripheralModel[];

  readonly isConnectedToDrain: boolean;

  readonly containsDevice: boolean = false;

  readonly showQrCodeScanner: boolean = false;

  readonly showSerialNumberIllustration: boolean = false;

  readonly isSimulation: boolean;

  readonly possiblePeripherals: Set<PeripheralType> = new Set();

  readonly configOptions: Product['configOptions'];

  readonly reuseConfig: Product['reuseConfig'];

  get hasValve(): boolean {
    return !!(this.openValveCommand || this.closeValveCommand);
  }

  get hasTap(): boolean {
    return !!(this.openTapCommand || this.closeTapCommand);
  }

  get hasPump(): boolean {
    return !!this.setPumpCommand;
  }

  constructor({
    nextProductName,
    peripherals,
    previousProductName,
    product,
    reservoir,
    issues,
    device,
    preventDeviceChange,
    isSimulation,
  }: ProductInfo & {
    nextProductName: string;
    previousProductName: string;
  }) {
    this.id = ResourceNameIdConverter.productNameToId(product.name);
    this.name = product.name;
    this.isSimulation = isSimulation;
    this.reservoirTitle = reservoir?.title;
    this.reservoirName = reservoir?.name;
    this.previousProductId =
      ResourceNameIdConverter.productNameToId(previousProductName);
    this.nextProductId =
      ResourceNameIdConverter.productNameToId(nextProductName);
    this.title = product.title || undefined;
    this.type = product.type;
    this.isConnectedToDrain = !!product.position?.drainName;
    this.issues =
      device.commissioningState === Device_CommissioningState.OPERATIONAL
        ? issues
        : [];

    if (device.commissioningState === Device_CommissioningState.UNSPECIFIED) {
      throw new Error('Unsupported commissioning state: UNSPECIFIED');
    }

    this.configOptions = product.configOptions;
    this.reuseConfig = product.reuseConfig;
    this.device = device;

    const productState = deriveProductState({
      commissioningState: device.commissioningState,
      deviceMode: device.mode,
      hasIssues: !!issues.length,
      isSimulation,
    });

    this.state = productState;

    switch (product.type) {
      case ProductType.SMART_DROP: {
        this.icon = 'DropIcon';
        this.reservoirPosition = 'bottom';
        this.containsDevice = true;
        this.showQrCodeScanner = true;
        this.showSerialNumberIllustration = true;
        break;
      }
      case ProductType.VALVE: {
        this.icon = 'DropIcon';
        this.reservoirPosition = 'bottom';
        this.allowChangingDevice = true;
        break;
      }
      case ProductType.TAP: {
        this.icon = 'TapIcon';
        this.reservoirPosition = 'top';
        this.allowChangingDevice = true;
        this.possiblePeripherals.add(PeripheralType.FLOW_SENSOR);
        break;
      }
      case ProductType.RAIN_METER: {
        this.icon = 'CloudRainIcon';
        this.reservoirPosition = 'top';
        this.allowChangingDevice = true;
        break;
      }
      case ProductType.SMART_LEVEL: {
        this.icon = 'LevelIcon';
        this.reservoirPosition = 'top';
        this.containsDevice = true;
        break;
      }
      case ProductType.PUMP: {
        this.icon = 'PumpIcon';
        this.reservoirPosition = 'side';
        this.allowChangingDevice = true;
        break;
      }
      case ProductType.CONTROLLER: {
        this.icon = 'ControllerIcon';
        this.containsDevice = true;
        this.showQrCodeScanner = true;
        break;
      }
      case ProductType.LEVEL: {
        this.icon = 'LevelIcon';
        this.reservoirPosition = 'top';
        this.allowChangingDevice = true;
        break;
      }
      case ProductType.REUSE: {
        this.icon = this.getReuseIcon();
        this.reservoirPosition = 'top';
        break;
      }
      default:
        throw new Error(
          `Unexpected product type: ${ProductType[product.type]}`,
        );
    }

    if (preventDeviceChange) {
      this.allowChangingDevice = false;
    }

    this.maxValveHeight = Number(reservoir?.maxWaterHeightMm);

    this.maintenanceModeCommand = device.maintenanceModeCommand;

    const availableMetricTargets = [
      ...peripherals.map((peripheral) =>
        createMetricTarget(peripheral.name, peripheral.availableMetricTypes),
      ),
      createMetricTarget(device.name, device.availableMetricTypes),
    ];

    this.availableMetricTargets = availableMetricTargets.filter(
      ({ types }) => types.length > 0,
    );

    this.availableMetricChartOptions = this.availableMetricTargets.flatMap(
      ({ types, resourceName }) =>
        types.map((type) => ({
          resourceName,
          type: type as SupportedMetricType,
        })),
    );

    const peripheralModels = peripherals.map(
      (peripheral) => new PeripheralModel(peripheral),
    );

    this.peripherals = peripheralModels;

    this.openValveCommand = peripheralModels.find(
      (peripheral) => peripheral.openValveCommand,
    )?.openValveCommand;
    this.closeValveCommand = peripheralModels.find(
      (peripheral) => peripheral.closeValveCommand,
    )?.closeValveCommand;
    this.openTapCommand = peripheralModels.find(
      (peripheral) => peripheral.openTapCommand,
    )?.openTapCommand;
    this.closeTapCommand = peripheralModels.find(
      (peripheral) => peripheral.closeTapCommand,
    )?.closeTapCommand;
    this.setValveHeightCommand = peripheralModels.find(
      (peripheral) => peripheral.setValveHeightCommand,
    )?.setValveHeightCommand;

    this.setPumpCommand = peripheralModels.find(
      (peripheral) => peripheral.setPumpCommand,
    )?.setPumpCommand;

    this.openPumpCommand = peripheralModels.find(
      (peripheral) => peripheral.openPumpCommand,
    )?.openPumpCommand;

    this.closePumpCommand = peripheralModels.find(
      (peripheral) => peripheral.closePumpCommand,
    )?.closePumpCommand;

    const canShowControlActions = () => {
      if (!this.hasTap && !this.hasValve && !this.hasPump) {
        return false;
      }

      if (
        this.state === 'READY_FOR_LINKING' ||
        this.state === 'PENDING_CLOUD_CONNECTION' ||
        this.state === 'READY_FOR_TESTING'
      ) {
        return false;
      }

      return true;
    };

    this.showControlActions = canShowControlActions();

    const resolveControlType = () => {
      if (this.hasTap) {
        return 'tap';
      }
      if (this.hasValve) {
        return 'valve';
      }

      if (this.hasPump) {
        return 'pump';
      }

      return undefined;
    };

    this.controlType = resolveControlType();

    const canBeSelected = () => {
      if (this.isSimulation) {
        return true;
      }

      const hasDevice =
        product.type === ProductType.SMART_DROP ||
        product.type === ProductType.SMART_LEVEL ||
        product.type === ProductType.CONTROLLER;

      if (hasDevice) {
        return true;
      }

      if (device.commissioningState === Device_CommissioningState.OPERATIONAL) {
        return true;
      }

      return false;
    };

    this.isSelectable = canBeSelected();
  }

  getDeviceOptions(devices: DeviceModel[]) {
    const deviceMap = new Map<string, DeviceModel>();

    devices
      .filter((device) => device.allowedProductTypesLinked.get(this.type))
      .forEach((device) => {
        deviceMap.set(device.name, device);
      });

    deviceMap.set(this.device.name, this.device);

    const emptyDevice = DeviceModel.createEmpty();
    deviceMap.set(emptyDevice.name, emptyDevice);

    return Array.from(deviceMap.values()).map((device) => ({
      name: device.name,
      label: device.title || device.name,
      disabled: device.name === this.device.name,
    }));
  }

  getReuseIcon(): ProductIconType {
    assertIsDefined(this.reuseConfig);
    switch (this.reuseConfig.type) {
      case ReuseProductConfig_ReuseType.CAR_WASH:
        return 'CarWashIcon';
      case ReuseProductConfig_ReuseType.IRRIGATION:
        return 'PlantWateringIcon';
      case ReuseProductConfig_ReuseType.TREE:
        return 'TreeIcon';
      case ReuseProductConfig_ReuseType.WC:
        return 'ToiletIcon';
      case ReuseProductConfig_ReuseType.OTHER:
        return 'WaterReuseIcon';
      case ReuseProductConfig_ReuseType.FIRE_FIGHTING:
        return 'FireFightingIcon';
      case ReuseProductConfig_ReuseType.UNSPECIFIED:
        return 'WaterReuseIcon';
      default:
        return assertUnreachable(this.reuseConfig.type);
    }
  }
}
